'use client'

import { useMemo, useState, useCallback, useEffect } from 'react'
import { AirlineDetail } from './airlineDetail'
import type { AirlinePage } from '@/src/types/airlineDetail'
import { useTranslations } from 'next-intl'
import { Airline } from '@/src/api/requests/topAirlines'

interface AirlineDetailWrapperProps {
    airlineData: AirlinePage
    locale: string
    isMobile: boolean
    topAirlines?: Airline[]
}

const AirlineDetailWrapper: React.FC<AirlineDetailWrapperProps> = ({ airlineData, locale, isMobile, topAirlines }) => {
    const t = useTranslations('airlinePages')
    const [activeTab, setActiveTab] = useState<string>('')
    const [showAllRoutes, setShowAllRoutes] = useState<boolean>(false)
    const isArabic = locale === 'ar'
    const airlines = useMemo(
        () => {
            if (!topAirlines) return []
            return topAirlines.map(airline => ({
                ...airline,
                name: isArabic ? airline.name_ar || airline.name_en : airline.name_en,
                href: isArabic ? airline.href_ar || airline.href_en : airline.href_en
            }))
        },
        [topAirlines, isArabic]
    )

    const {
        airlineData: data,
        longName,
        aboutAirline,
        frequentFlightRoutes,
        airlineNews,
        airlineHighlights,
        generalInformation,
        popularAirlineDestinations,
        faq,
        historicMilestones,
        popularAirlines,
        awards,
        bookingTable
    } = airlineData

    // Extract hero data
    const hero = data.hero
    const code = data.code

    // Initialize active tab from highlights (first tab key if available)
    useEffect(() => {
        if (!activeTab && airlineHighlights?.tabs && airlineHighlights.tabs.length > 0) {
            setActiveTab(airlineHighlights.tabs[0].key)
        }
    }, [])

    // Event handlers
    const handleTabChange = useCallback((tab: string) => {
        setActiveTab(tab)
    }, [])

    const handleToggleRoutes = useCallback(() => {
        setShowAllRoutes(prev => !prev)
    }, [])

    return (
        <AirlineDetail
            isError={false}
            hero={hero}
            code={code}
            longName={longName}
            aboutAirline={aboutAirline}
            frequentFlightRoutes={frequentFlightRoutes}
            airlineNews={airlineNews}
            airlineBooking={bookingTable}
            airlineAwards={awards}
            airlineHighlights={airlineHighlights}
            generalInformation={generalInformation}
            popularAirlineDestinations={popularAirlineDestinations}
            faq={faq}
            historicMilestones={historicMilestones}
            popularAirlines={popularAirlines}
            activeTab={activeTab}
            showAllRoutes={showAllRoutes}
            onTabChange={handleTabChange}
            onToggleRoutes={handleToggleRoutes}
            topAirlines={airlines}
            isMobile={isMobile}
            t={t}
            locale={locale}
        />
    )
}

export default AirlineDetailWrapper